import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getAdminBranchAccessThunk,
  addBranchAccessThunk,
  updateBranchAccessPermissionsThunk,
  removeBranchAccessThunk,
  setAdminPrimaryBranchThunk,
  getAdminsByBranchThunk,
  bulkAssignBranchAccessThunk,
} from "../../../store/thunks/adminThunks";
import { clearBranchAccessState } from "../../../store/slices/adminSlice";
import Button from "../../../components/common/Button";
import Badge from "../../../components/common/Badge";
import Modal from "../../../components/common/Modal";
import Dropdown from "../../../components/common/Dropdown";
import Checkbox from "../../../components/common/Checkbox";
import {
  OfficeBuildingIcon,
  PlusIcon,
  TrashIcon,
  PencilIcon,
  StarIcon,
  ShieldCheckIcon,
  UserGroupIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
  EyeIcon,
  CogIcon,
  LockClosedIcon,
  RefreshIcon,
  DotsVerticalIcon,
  XIcon,
} from "@heroicons/react/outline";

const BranchAccessManager = ({ admin, onClose }) => {
  const dispatch = useDispatch();
  const {
    selectedAdminBranchAccess,
    branchAdminsList,
    loading,
    loadingBranchAccess,
  } = useSelector((state) => state.admin);
  const { branches } = useSelector((state) => state.branch);
  const { isRootAdmin } = useSelector((state) => state.auth);

  // Local state
  const [activeTab, setActiveTab] = useState("admin-access");
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedBranchForEdit, setSelectedBranchForEdit] = useState(null);
  const [selectedBranchForView, setSelectedBranchForView] = useState(null);
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [activeActionMenu, setActiveActionMenu] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    branchCode: null,
    accessLevel: null,
    canTakeAppointments: false,
  });
  const [formErrors, setFormErrors] = useState({});

  // Bulk form state
  const [bulkFormData, setBulkFormData] = useState({
    adminCodes: [],
    accessLevel: null,
    canTakeAppointments: false,
  });

  // Access level options
  const accessLevelOptions = [
    { _id: "VIEW", name: "View Only", description: "Can only view data" },
    { _id: "MANAGE", name: "Manage", description: "Can view and edit data" },
    {
      _id: "FULL_CONTROL",
      name: "Full Control",
      description: "Complete access including delete",
    },
  ];

  // Convert branches to dropdown format
  const branchOptions = (branches || []).map((branch) => ({
    _id: branch.code || branch.branchCode,
    name: `${branch.name || branch.branchName} (${
      branch.code || branch.branchCode
    })`,
    data: branch,
  }));

  // Load admin branch access on mount
  useEffect(() => {
    if (admin?.adminCode) {
      dispatch(getAdminBranchAccessThunk(admin.adminCode));
    }

    return () => {
      dispatch(clearBranchAccessState());
    };
  }, [admin?.adminCode, dispatch]);

  // Close action menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setActiveActionMenu(null);
    if (activeActionMenu) {
      document.addEventListener("click", handleClickOutside);
      return () => document.removeEventListener("click", handleClickOutside);
    }
  }, [activeActionMenu]);

  // Get available branches (not already assigned)
  const getAvailableBranches = () => {
    if (!selectedAdminBranchAccess?.accessibleBranches) return branchOptions;

    const assignedCodes = selectedAdminBranchAccess.accessibleBranches.map(
      (b) => b.branchCode
    );

    return branchOptions.filter(
      (branch) => !assignedCodes.includes(branch._id)
    );
  };

  // Get access level styling
  const getAccessLevelStyle = (level) => {
    switch (level) {
      case "VIEW":
        return {
          variant: "info",
          bg: "bg-blue-50",
          text: "text-blue-700",
          border: "border-blue-200",
          icon: <EyeIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />,
          label: "View",
        };
      case "MANAGE":
        return {
          variant: "warning",
          bg: "bg-amber-50",
          text: "text-amber-700",
          border: "border-amber-200",
          icon: <CogIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />,
          label: "Manage",
        };
      case "FULL_CONTROL":
        return {
          variant: "success",
          bg: "bg-emerald-50",
          text: "text-emerald-700",
          border: "border-emerald-200",
          icon: <LockClosedIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />,
          label: "Full Control",
        };
      default:
        return {
          variant: "default",
          bg: "bg-gray-50",
          text: "text-gray-700",
          border: "border-gray-200",
          icon: null,
          label: level,
        };
    }
  };

  // Validate form
  const validateForm = () => {
    const errors = {};

    if (!formData.branchCode) {
      errors.branchCode = "Branch is required";
    }

    if (!formData.accessLevel) {
      errors.accessLevel = "Access level is required";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      branchCode: null,
      accessLevel: null,
      canTakeAppointments: false,
    });
    setFormErrors({});
  };

  // Handle add branch access
  const handleAddBranchAccess = async () => {
    if (!validateForm()) return;

    try {
      await dispatch(
        addBranchAccessThunk({
          adminCode: admin.adminCode,
          branchData: {
            branchCode: formData.branchCode._id,
            accessLevel: formData.accessLevel._id,
            canTakeAppointments: formData.canTakeAppointments,
          },
        })
      ).unwrap();

      setIsAddModalOpen(false);
      resetForm();
      dispatch(getAdminBranchAccessThunk(admin.adminCode));
    } catch (error) {
      console.error("Failed to add branch access:", error);
    }
  };

  // Handle update branch access
  const handleUpdateBranchAccess = async () => {
    if (!formData.accessLevel) {
      setFormErrors({ accessLevel: "Access level is required" });
      return;
    }

    try {
      await dispatch(
        updateBranchAccessPermissionsThunk({
          adminCode: admin.adminCode,
          branchCode: selectedBranchForEdit.branchCode,
          updateData: {
            accessLevel: formData.accessLevel._id,
            canTakeAppointments: formData.canTakeAppointments,
          },
        })
      ).unwrap();

      setIsEditModalOpen(false);
      setSelectedBranchForEdit(null);
      resetForm();
      dispatch(getAdminBranchAccessThunk(admin.adminCode));
    } catch (error) {
      console.error("Failed to update branch access:", error);
    }
  };

  // Handle remove branch access
  const handleRemoveBranchAccess = async (branchCode) => {
    if (
      !window.confirm(
        "Are you sure you want to remove this branch access? This action cannot be undone."
      )
    ) {
      return;
    }

    try {
      await dispatch(
        removeBranchAccessThunk({
          adminCode: admin.adminCode,
          branchCode,
        })
      ).unwrap();

      dispatch(getAdminBranchAccessThunk(admin.adminCode));
    } catch (error) {
      console.error("Failed to remove branch access:", error);
    }
  };

  // Handle set primary branch
  const handleSetPrimaryBranch = async (branchCode) => {
    if (
      !window.confirm(
        "Are you sure you want to set this as the primary branch?"
      )
    ) {
      return;
    }

    try {
      await dispatch(
        setAdminPrimaryBranchThunk({
          adminCode: admin.adminCode,
          branchCode,
          accessLevel: "MANAGE",
        })
      ).unwrap();

      dispatch(getAdminBranchAccessThunk(admin.adminCode));
    } catch (error) {
      console.error("Failed to set primary branch:", error);
    }
  };

  // Handle view branch admins
  const handleViewBranchAdmins = (branchCode) => {
    setSelectedBranchForView(branchCode);
    dispatch(
      getAdminsByBranchThunk({ branchCode, params: { includeSecondary: true } })
    );
  };

  // Handle bulk assign
  const handleBulkAssign = async () => {
    if (bulkFormData.adminCodes.length === 0 || !bulkFormData.accessLevel) {
      return;
    }

    try {
      await dispatch(
        bulkAssignBranchAccessThunk({
          branchCode: selectedBranchForView,
          bulkData: {
            adminCodes: bulkFormData.adminCodes,
            accessLevel: bulkFormData.accessLevel._id,
            canTakeAppointments: bulkFormData.canTakeAppointments,
          },
        })
      ).unwrap();

      setIsBulkModalOpen(false);
      setBulkFormData({
        adminCodes: [],
        accessLevel: null,
        canTakeAppointments: false,
      });

      dispatch(
        getAdminsByBranchThunk({
          branchCode: selectedBranchForView,
          params: { includeSecondary: true },
        })
      );
    } catch (error) {
      console.error("Failed to bulk assign:", error);
    }
  };

  // Open edit modal
  const openEditModal = (branchAccess) => {
    setSelectedBranchForEdit(branchAccess);
    setFormData({
      branchCode: null,
      accessLevel: accessLevelOptions.find(
        (opt) => opt._id === branchAccess.accessLevel
      ),
      canTakeAppointments: branchAccess.canTakeAppointments || false,
    });
    setIsEditModalOpen(true);
    setActiveActionMenu(null);
  };

  // Render mobile action menu
  const renderActionMenu = (branchAccess) => {
    const isPrimary = branchAccess.isPrimary;
    const isActive = activeActionMenu === branchAccess.branchCode;

    return (
      <div className="relative">
        <button
          onClick={(e) => {
            e.stopPropagation();
            setActiveActionMenu(isActive ? null : branchAccess.branchCode);
          }}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <DotsVerticalIcon className="w-5 h-5 text-gray-500" />
        </button>

        {isActive && (
          <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-xl shadow-lg border border-gray-200 py-1 z-20">
            {!isPrimary && (
              <button
                onClick={() => {
                  handleSetPrimaryBranch(branchAccess.branchCode);
                  setActiveActionMenu(null);
                }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <StarIcon className="w-4 h-4 text-amber-500" />
                Set as Primary
              </button>
            )}
            <button
              onClick={() => openEditModal(branchAccess)}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <PencilIcon className="w-4 h-4 text-blue-500" />
              Edit Access
            </button>
            {!isPrimary && (
              <button
                onClick={() => {
                  handleRemoveBranchAccess(branchAccess.branchCode);
                  setActiveActionMenu(null);
                }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
              >
                <TrashIcon className="w-4 h-4" />
                Remove Access
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  // Render branch access card
  const renderBranchAccessCard = (branchAccess) => {
    const isPrimary = branchAccess.isPrimary;
    const accessStyle = getAccessLevelStyle(branchAccess.accessLevel);

    return (
      <div
        key={branchAccess.branchCode}
        className={`
          border-2 rounded-xl p-3 sm:p-4 transition-all
          ${
            isPrimary
              ? "border-amber-300 bg-amber-50/50"
              : "border-gray-200 bg-white hover:border-gray-300"
          }
        `}
      >
        {/* Header with icon and actions */}
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
            <div
              className={`
                flex-shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center
                ${isPrimary ? "bg-amber-100" : "bg-gray-100"}
              `}
            >
              <OfficeBuildingIcon
                className={`w-5 h-5 ${
                  isPrimary ? "text-amber-600" : "text-gray-600"
                }`}
              />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h4 className="font-semibold text-gray-900 text-sm sm:text-base truncate">
                  {branchAccess.branchName || branchAccess.branchCode}
                </h4>
                {isPrimary && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-amber-100 border border-amber-300">
                    <StarIcon className="w-3 h-3 text-amber-600 fill-amber-500" />
                    <span className="text-[10px] sm:text-xs font-semibold text-amber-700">
                      Primary
                    </span>
                  </span>
                )}
              </div>
              <p className="text-xs sm:text-sm text-gray-500 font-mono mt-0.5">
                {branchAccess.branchCode}
              </p>
            </div>
          </div>

          {/* Actions - Desktop */}
          {isRootAdmin && (
            <>
              {/* Desktop buttons */}
              <div className="hidden sm:flex items-center gap-1.5">
                {!isPrimary && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() =>
                      handleSetPrimaryBranch(branchAccess.branchCode)
                    }
                    title="Set as Primary Branch"
                    className="!p-2"
                  >
                    <StarIcon className="w-4 h-4" />
                  </Button>
                )}
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => openEditModal(branchAccess)}
                  title="Edit Access"
                  className="!p-2"
                >
                  <PencilIcon className="w-4 h-4" />
                </Button>
                {!isPrimary && (
                  <Button
                    variant="danger-text"
                    size="sm"
                    onClick={() =>
                      handleRemoveBranchAccess(branchAccess.branchCode)
                    }
                    title="Remove Access"
                    className="!p-2"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </Button>
                )}
              </div>

              {/* Mobile action menu */}
              <div className="sm:hidden">{renderActionMenu(branchAccess)}</div>
            </>
          )}
        </div>

        {/* Access Level & Permissions */}
        <div className="flex flex-wrap items-center gap-2">
          <div
            className={`
              inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs sm:text-sm font-medium
              ${accessStyle.bg} ${accessStyle.text} border ${accessStyle.border}
            `}
          >
            {accessStyle.icon}
            <span>{accessStyle.label}</span>
          </div>

          {branchAccess.canTakeAppointments && (
            <span className="inline-flex items-center px-2 py-1 rounded-lg text-xs font-medium bg-violet-50 text-violet-700 border border-violet-200">
              📅 Appointments
            </span>
          )}
        </div>
      </div>
    );
  };

  // Render branch admin card
  const renderBranchAdminCard = (adminData) => {
    const accessStyle = getAccessLevelStyle(adminData.accessLevel);

    return (
      <div
        key={adminData.adminCode}
        className="bg-white border rounded-xl p-3 sm:p-4"
      >
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex-shrink-0 bg-gray-100 rounded-full p-2">
              <UserGroupIcon className="w-5 h-5 text-gray-600" />
            </div>
            <div className="min-w-0">
              <h4 className="font-semibold text-gray-900 text-sm sm:text-base truncate">
                {adminData.name}
              </h4>
              <p className="text-xs sm:text-sm text-gray-600 truncate">
                {adminData.email}
              </p>
              <p className="text-xs text-gray-500 font-mono">
                {adminData.adminCode}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 ml-11 sm:ml-0">
            <Badge variant={adminData.isPrimaryBranch ? "warning" : "default"}>
              {adminData.isPrimaryBranch ? "Primary" : "Secondary"}
            </Badge>
            <Badge variant={accessStyle.variant}>{accessStyle.label}</Badge>
          </div>
        </div>
      </div>
    );
  };

  // Loading state
  if (loadingBranchAccess && !selectedAdminBranchAccess) {
    return (
      <div className="flex justify-center items-center py-12 px-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600 text-sm sm:text-base">
            Loading branch access...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Admin Info Header */}
      <div className="bg-gradient-to-br from-blue-50 via-indigo-50 to-violet-50 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-blue-200">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3 sm:gap-4 min-w-0">
            <div className="flex-shrink-0 bg-blue-100 rounded-xl p-2.5 sm:p-3">
              <ShieldCheckIcon className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600" />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 truncate">
                {admin?.name}
              </h2>
              <p className="text-xs sm:text-sm text-gray-600 truncate">
                {admin?.email}
              </p>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <Badge variant="info" className="text-xs">
                  {admin?.role}
                </Badge>
                <span className="text-xs text-gray-500 font-mono">
                  {admin?.adminCode}
                </span>
              </div>
            </div>
          </div>

          {isRootAdmin && (
            <Button
              variant="primary"
              onClick={() => setIsAddModalOpen(true)}
              disabled={getAvailableBranches().length === 0}
              className="w-full sm:w-auto justify-center"
            >
              <PlusIcon className="w-4 h-4 mr-2" />
              <span className="sm:inline">Add Branch</span>
            </Button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
        <div className="bg-white border rounded-xl p-3 sm:p-4">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 p-2 bg-blue-50 rounded-lg">
              <OfficeBuildingIcon className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600" />
            </div>
            <div className="min-w-0">
              <p className="text-xs sm:text-sm text-gray-600">Total Branches</p>
              <p className="text-xl sm:text-2xl font-bold text-gray-900">
                {selectedAdminBranchAccess?.accessibleBranches?.length || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white border rounded-xl p-3 sm:p-4">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 p-2 bg-amber-50 rounded-lg">
              <StarIcon className="w-6 h-6 sm:w-8 sm:h-8 text-amber-600" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs sm:text-sm text-gray-600">Primary Branch</p>
              <p className="text-sm sm:text-lg font-bold text-gray-900 truncate">
                {selectedAdminBranchAccess?.branchAccess?.primaryBranch ||
                  "Not Set"}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white border rounded-xl p-3 sm:p-4">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 p-2 bg-emerald-50 rounded-lg">
              <LockClosedIcon className="w-6 h-6 sm:w-8 sm:h-8 text-emerald-600" />
            </div>
            <div className="min-w-0">
              <p className="text-xs sm:text-sm text-gray-600">Default Access</p>
              <p className="text-sm sm:text-lg font-bold text-gray-900">
                {selectedAdminBranchAccess?.branchAccess?.accessLevel || "VIEW"}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Branch Access List */}
      <div>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 flex items-center gap-2">
            <OfficeBuildingIcon className="w-5 h-5 text-primary" />
            Branch Access
          </h3>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => dispatch(getAdminBranchAccessThunk(admin.adminCode))}
            disabled={loadingBranchAccess}
            className="w-full sm:w-auto justify-center"
          >
            <RefreshIcon
              className={`w-4 h-4 mr-2 ${
                loadingBranchAccess ? "animate-spin" : ""
              }`}
            />
            Refresh
          </Button>
        </div>

        {selectedAdminBranchAccess?.accessibleBranches?.length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
            {selectedAdminBranchAccess.accessibleBranches.map(
              renderBranchAccessCard
            )}
          </div>
        ) : (
          <div className="text-center py-8 sm:py-12 bg-gray-50 rounded-xl">
            <OfficeBuildingIcon className="w-10 h-10 sm:w-12 sm:h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600 text-sm sm:text-base">
              No branch access assigned
            </p>
            {isRootAdmin && (
              <Button
                variant="secondary"
                size="sm"
                className="mt-4"
                onClick={() => setIsAddModalOpen(true)}
              >
                <PlusIcon className="w-4 h-4 mr-2" />
                Add First Branch
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 rounded-xl p-3 sm:p-4 border border-blue-200">
        <div className="flex gap-3">
          <InformationCircleIcon className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-xs sm:text-sm text-blue-800 min-w-0">
            <p className="font-semibold mb-2">About Branch Access</p>
            <div className="space-y-1.5">
              <div className="flex items-start gap-2">
                <EyeIcon className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                <span>
                  <strong>View:</strong> Can only view data in the branch
                </span>
              </div>
              <div className="flex items-start gap-2">
                <CogIcon className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <span>
                  <strong>Manage:</strong> Can view and edit data
                </span>
              </div>
              <div className="flex items-start gap-2">
                <LockClosedIcon className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                <span>
                  <strong>Full Control:</strong> Complete access including
                  delete and assign
                </span>
              </div>
              <div className="flex items-start gap-2 pt-1 border-t border-blue-200 mt-2">
                <StarIcon className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                <span>
                  Primary branch is the admin's default working location
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Branch Access Modal */}
      <Modal
        isOpen={isAddModalOpen}
        closeModal={() => {
          setIsAddModalOpen(false);
          resetForm();
        }}
        title="Add Branch Access"
      >
        <div className="space-y-4">
          <Dropdown
            label="Branch"
            options={getAvailableBranches()}
            selected={formData.branchCode}
            onChange={(branch) =>
              setFormData({ ...formData, branchCode: branch })
            }
            error={formErrors.branchCode}
            placeholder="Select a branch"
            required
            clearable
          />

          <Dropdown
            label="Access Level"
            options={accessLevelOptions}
            selected={formData.accessLevel}
            onChange={(level) =>
              setFormData({ ...formData, accessLevel: level })
            }
            error={formErrors.accessLevel}
            placeholder="Select access level"
            required
            clearable
          />

          <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
            <Checkbox
              checked={formData.canTakeAppointments}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  canTakeAppointments: e.target.checked,
                })
              }
            />
            <div>
              <label className="text-sm font-medium text-gray-700">
                Can Take Appointments
              </label>
              <p className="text-xs text-gray-500 mt-0.5">
                Enable if the admin can be assigned appointments at this branch
              </p>
            </div>
          </div>

          <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3 pt-4 border-t">
            <Button
              variant="secondary"
              onClick={() => {
                setIsAddModalOpen(false);
                resetForm();
              }}
              className="w-full sm:w-auto justify-center"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleAddBranchAccess}
              disabled={loading}
              className="w-full sm:w-auto justify-center"
            >
              {loading ? "Adding..." : "Add Access"}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Edit Branch Access Modal */}
      <Modal
        isOpen={isEditModalOpen}
        closeModal={() => {
          setIsEditModalOpen(false);
          setSelectedBranchForEdit(null);
          resetForm();
        }}
        title="Edit Branch Access"
      >
        <div className="space-y-4">
          <div className="bg-gray-50 rounded-xl p-3 sm:p-4">
            <p className="text-xs sm:text-sm text-gray-600 mb-1">Branch</p>
            <p className="font-semibold text-gray-900 text-sm sm:text-base">
              {selectedBranchForEdit?.branchName ||
                selectedBranchForEdit?.branchCode}
            </p>
            <p className="text-xs text-gray-500 font-mono mt-1">
              {selectedBranchForEdit?.branchCode}
            </p>
          </div>

          <Dropdown
            label="Access Level"
            options={accessLevelOptions}
            selected={formData.accessLevel}
            onChange={(level) =>
              setFormData({ ...formData, accessLevel: level })
            }
            error={formErrors.accessLevel}
            placeholder="Select access level"
            required
            clearable
          />

          <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
            <Checkbox
              checked={formData.canTakeAppointments}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  canTakeAppointments: e.target.checked,
                })
              }
            />
            <div>
              <label className="text-sm font-medium text-gray-700">
                Can Take Appointments
              </label>
              <p className="text-xs text-gray-500 mt-0.5">
                Enable if the admin can be assigned appointments at this branch
              </p>
            </div>
          </div>

          <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3 pt-4 border-t">
            <Button
              variant="secondary"
              onClick={() => {
                setIsEditModalOpen(false);
                setSelectedBranchForEdit(null);
                resetForm();
              }}
              className="w-full sm:w-auto justify-center"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleUpdateBranchAccess}
              disabled={loading}
              className="w-full sm:w-auto justify-center"
            >
              {loading ? "Updating..." : "Update Access"}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default BranchAccessManager;
